/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RecommendationPanel - Panel de Recomendaciones Priorizadas
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file RecommendationPanel.tsx
 * @version 1.0.0
 * @description Panel que muestra lista de recomendaciones ordenadas por
 *              prioridad (crítico → warning → bueno) con iconos y acciones
 * 
 * @component RecommendationPanel
 * @architecture Fase 2 - Componentes Visuales del Risk Meter
 * 
 * FEATURES:
 * - Lista de recomendaciones ordenadas por prioridad
 * - Iconos según prioridad (🔴/🟡/🟢)
 * - Badges de prioridad con colores
 * - Botones de acción opcionales
 * - Animaciones smooth al aparecer
 * - Responsive design
 * 
 * PRIORIDADES:
 * - critical: Recomendaciones urgentes (rojo)
 * - warning:  Recomendaciones importantes (amarillo/naranja)
 * - good:     Sugerencias de mejora (verde/azul)
 */

import React, { useState } from 'react';
import { Card, ListGroup, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type RecommendationPriority = 'critical' | 'warning' | 'good' | 'info';

interface Recommendation {
  /** Texto de la recomendación */
  text: string;
  
  /** Prioridad de la recomendación */
  priority: RecommendationPriority;
  
  /** ID opcional para aplicar acción */
  id?: string;
  
  /** Si la recomendación puede ser aplicada automáticamente */
  actionable?: boolean;
}

interface RecommendationPanelProps {
  /** Lista de recomendaciones (como strings o objetos) */
  recommendations: (string | Recommendation)[];
  
  /** Callback cuando se hace click en "Aplicar" */
  onApply?: (recommendation: string | Recommendation, index: number) => void;
  
  /** Título del panel */
  title?: string;
  
  /** Mostrar botones de acción */
  showActions?: boolean;
  
  /** Clase CSS adicional */
  className?: string;
  
  /** Altura máxima con scroll */
  maxHeight?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const PRIORITY_COLORS: Record<RecommendationPriority, string> = {
  critical: '#ef4444',
  warning: '#f59e0b',
  good: '#10b981',
  info: '#3b82f6'
};

const PRIORITY_BG_COLORS: Record<RecommendationPriority, string> = {
  critical: 'danger',
  warning: 'warning',
  good: 'success',
  info: 'info'
};

const PRIORITY_LABELS: Record<RecommendationPriority, string> = {
  critical: 'Crítico',
  warning: 'Importante',
  good: 'Sugerencia',
  info: 'Información'
};

const PRIORITY_ICONS: Record<RecommendationPriority, any> = {
  critical: faExclamationCircle,
  warning: faExclamationTriangle,
  good: faLightbulb,
  info: faInfoCircle
};

const PRIORITY_ORDER: Record<RecommendationPriority, number> = {
  critical: 1,
  warning: 2,
  good: 3,
  info: 4
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convierte una recomendación string a objeto Recommendation
 * Intenta inferir la prioridad del texto
 */
const normalizeRecommendation = (rec: string | Recommendation): Recommendation => {
  if (typeof rec === 'object') {
    return rec;
  }
  
  // Inferir prioridad del texto
  const text = rec.toLowerCase();
  let priority: RecommendationPriority = 'info';
  
  if (
    text.includes('crítico') || 
    text.includes('urgente') || 
    text.includes('inmediatamente') ||
    text.includes('reducir enlaces') ||
    text.includes('reducir mensaje')
  ) {
    priority = 'critical';
  } else if (
    text.includes('recomendado') || 
    text.includes('advertencia') ||
    text.includes('debería') ||
    text.includes('considera')
  ) {
    priority = 'warning';
  } else if (
    text.includes('mejorar') || 
    text.includes('óptimo') ||
    text.includes('sugerencia')
  ) {
    priority = 'good';
  }
  
  return {
    text: rec,
    priority,
    actionable: false
  };
};

/**
 * Ordena recomendaciones por prioridad
 */
const sortRecommendations = (recs: Recommendation[]): Recommendation[] => {
  return [...recs].sort((a, b) => {
    return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const RecommendationPanel: React.FC<RecommendationPanelProps> = ({
  recommendations,
  onApply,
  title = '💡 Recomendaciones',
  showActions = false,
  className = '',
  maxHeight = '400px'
}) => {
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [appliedIndices, setAppliedIndices] = useState<Set<number>>(new Set());
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleApply = (rec: Recommendation, index: number) => {
    if (onApply) {
      onApply(rec, index);
      setAppliedIndices(prev => new Set(prev).add(index));
    }
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // PROCESS RECOMMENDATIONS
  // ───────────────────────────────────────────────────────────────────────
  
  const normalizedRecs = recommendations.map(normalizeRecommendation);
  const sortedRecs = sortRecommendations(normalizedRecs);
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────
  
  if (sortedRecs.length === 0) {
    return (
      <Card 
        className={`recommendation-panel ${className}`}
        style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--bs-body-bg)'
        }}
      >
        <Card.Body style={{ padding: '40px', textAlign: 'center' }}>
          <FontAwesomeIcon 
            icon={faCheckCircle} 
            style={{ 
              fontSize: '3rem', 
              color: '#22c55e',
              marginBottom: '16px'
            }}
          />
          <h5 style={{ fontWeight: 600, marginBottom: '8px' }}>
            ¡Todo Perfecto!
          </h5>
          <p style={{ color: 'var(--bs-secondary)', marginBottom: 0 }}>
            No hay recomendaciones en este momento. Tu configuración es óptima.
          </p>
        </Card.Body>
      </Card>
    );
  }
  
  return (
    <Card 
      className={`recommendation-panel ${className}`}
      style={{
        border: 'none',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: 'var(--bs-body-bg)'
      }}
    >
      {/* Header */}
      <Card.Header
        style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '16px 20px'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0" style={{ fontWeight: 600 }}>
            {title}
          </h5>
          <Badge 
            bg="primary" 
            style={{ 
              fontSize: '0.85rem',
              padding: '6px 12px'
            }}
          >
            {sortedRecs.length} {sortedRecs.length === 1 ? 'item' : 'items'}
          </Badge>
        </div>
      </Card.Header>
      
      {/* List */}
      <div style={{ maxHeight, overflowY: 'auto' }}>
        <ListGroup variant="flush">
          {sortedRecs.map((rec, index) => {
            const color = PRIORITY_COLORS[rec.priority];
            const bgVariant = PRIORITY_BG_COLORS[rec.priority];
            const label = PRIORITY_LABELS[rec.priority];
            const icon = PRIORITY_ICONS[rec.priority];
            const isApplied = appliedIndices.has(index);
            
            return (
              <ListGroup.Item
                key={index}
                style={{
                  padding: '16px 20px',
                  border: 'none',
                  borderBottom: '1px solid var(--bs-border-color)',
                  backgroundColor: 'var(--bs-body-bg)',
                  transition: 'all 0.2s ease',
                  opacity: isApplied ? 0.6 : 1
                }}
                className="recommendation-item"
              >
                <div className="d-flex align-items-start gap-3">
                  {/* Icon */}
                  <div 
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: `${color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <FontAwesomeIcon 
                      icon={icon}
                      style={{ color, fontSize: '1rem' }}
                    />
                  </div>
                  
                  {/* Content */}
                  <div style={{ flex: 1 }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Badge 
                        bg={bgVariant}
                        style={{ 
                          fontSize: '0.7rem',
                          padding: '4px 8px',
                          fontWeight: 600
                        }}
                      >
                        {label}
                      </Badge>
                    </div>
                    
                    <p 
                      style={{ 
                        fontSize: '0.9rem',
                        marginBottom: 0,
                        lineHeight: '1.5',
                        color: 'var(--bs-body-color)'
                      }}
                    >
                      {rec.text}
                    </p>
                    
                    {/* Action button si es aplicable */}
                    {showActions && rec.actionable && !isApplied && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        style={{
                          marginTop: '8px',
                          fontSize: '0.8rem',
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}
                        onClick={() => handleApply(rec, index)}
                      >
                        Aplicar
                      </Button>
                    )}
                    
                    {isApplied && (
                      <Badge 
                        bg="success"
                        style={{ 
                          marginTop: '8px',
                          fontSize: '0.75rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        Aplicado
                      </Badge>
                    )}
                  </div>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </div>
      
      {/* Footer con resumen de prioridades */}
      <Card.Footer
        style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderTop: '1px solid var(--bs-border-color)',
          padding: '12px 20px'
        }}
      >
        <div className="d-flex gap-3 justify-content-center flex-wrap">
          {Object.entries(PRIORITY_COLORS).map(([priority, color]) => {
            const count = sortedRecs.filter(r => r.priority === priority).length;
            if (count === 0) return null;
            
            return (
              <div 
                key={priority}
                className="d-flex align-items-center gap-1"
                style={{ fontSize: '0.85rem' }}
              >
                <div 
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: color
                  }}
                />
                <span style={{ color: 'var(--bs-secondary)' }}>
                  {PRIORITY_LABELS[priority as RecommendationPriority]}: {count}
                </span>
              </div>
            );
          })}
        </div>
      </Card.Footer>
      
      {/* CSS adicional */}
      <style>{`
        .recommendation-panel .list-group-item:last-child {
          border-bottom: none !important;
        }
        
        .recommendation-item:hover {
          background-color: var(--bs-body-bg) !important;
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .recommendation-item {
          animation: slideIn 0.3s ease forwards;
        }
      `}</style>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default RecommendationPanel;
